import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, CheckCircle2, XCircle, Plus, Zap, CalendarCheck, MessageSquare } from 'lucide-react';
import Navbar from './Navbar';
import BackgroundEffects from './BackgroundEffects';
import Footer from './Footer';
import CalendlyEmbed from './CalendlyEmbed';
import ContactModal from './ContactModal';
import TermsOfUse from './TermsOfUse';
import AiCallAssistantApplyForm from './AiCallAssistantApplyForm';

const LIVE_PHONE_DISPLAY = '(914) 529-0661';
const LIVE_PHONE_TEL = 'tel:+19145290661';

const TRUST_POINTS = [
  {
    icon: Zap,
    title: 'Instant answer',
    description: 'No hold music, no voicemail — every call is picked up right away.',
  },
  {
    icon: CalendarCheck,
    title: 'Books your appointment live',
    description: 'Clara checks real availability and puts it on the calendar during the call itself.',
  },
  {
    icon: MessageSquare,
    title: 'Instant SMS confirmation',
    description: "You'll get a real text confirming the appointment the moment it's booked.",
  },
];

const PAGE_TITLE = 'AI Call Assistant for Service Businesses | Primeslink';
const PAGE_DESCRIPTION =
  "An AI voice agent that answers every call, qualifies the lead, books the appointment, updates your CRM, and texts a confirmation — in one call. Not a message-taking service. Book a free demo.";

const FAQS = [
  {
    q: 'Does this just take a message, like a virtual receptionist?',
    a: "No — that's the whole point. Most \"AI receptionist\" services still just take a message and leave the follow-up to you. Our AI Call Assistant qualifies the lead in conversation, books the appointment directly on your calendar, updates your CRM automatically, and texts the customer a confirmation — all inside the same call. Nothing gets handed back to you to finish.",
  },
  {
    q: 'What industries does the AI Call Assistant work for?',
    a: "It's built for any high-ticket, service-based business where a missed call is a missed sale. Our platform already supports 30+ industries, each with its own tailored intake and qualification setup — insurance is our most fully-built live example, shown throughout this page. Book a free demo and we'll show you how it adapts to yours.",
  },
  {
    q: 'Does it integrate with my CRM and calendar?',
    a: "Yes. Every call automatically updates a CRM record with the lead's details, an AI-generated summary, a qualification score, and the recommended next action — no manual data entry. Appointments booked during a call create a real Google Calendar invite on the spot, and every commitment made on a call (\"call me Thursday,\" \"send a quote tomorrow\") is automatically tracked as a follow-up task with reminders.",
  },
  {
    q: 'Can I still talk to my own leads directly?',
    a: 'Always. Every lead shows up on a live dashboard with one-tap actions to call, text, or email them directly, and a built-in dialer can bridge you into a live call with auto-transcription. The AI handles the first response and the busywork; you stay in full control of every relationship.',
  },
  {
    q: 'How much does this cost?',
    a: "There's no fixed public price — this is a done-for-you implementation built and priced around your specific business, call volume, and CRM setup, not a one-size-fits-all subscription tier. Book a free demo and we'll walk through your setup and what it would look like.",
  },
  {
    q: 'Is there a real example I can see or hear?',
    a: "Yes — the AI Call Assistant shown on this page is a real, fully working build for the insurance industry, running on the same technology we'd implement for your business. Call the live number above, or book a free demo and we'll walk you through it.",
  },
];

const FEATURE_GROUPS = [
  {
    icon: '\u{1F9E0}',
    title: 'Every Lead, Understood Instantly',
    blurb: "The AI doesn't just log a call — it tells you what happened and what to do next.",
    items: [
      ['AI Lead Summary', 'a plain-English summary, not a raw transcript to dig through'],
      ['AI Qualification Reason', 'the bullet-point "why" behind every qualified or disqualified lead'],
      ['AI Lead Score', '0–100, broken into intent, urgency, budget, and likelihood'],
      ['Recommended Next Action', '"Call immediately," "Send a quote," "Book a consultation"'],
      ['Intent Detection', 'Ready to Buy, Shopping Around, Price Sensitive, Emergency, and more'],
    ],
  },
  {
    icon: '⚡',
    title: 'One-Tap Everything',
    blurb: 'Every lead lands on a live dashboard, fully actionable — no separate CRM to switch to.',
    items: [
      ['Quick Actions', 'call, text, email, book, or mark won/lost in one tap'],
      ['One-Click Calling', 'bridges you straight into a live call, recorded and auto-transcribed'],
      ['Full Transcript Search', 'find any call by what was actually said, instantly'],
      ['Analytics Dashboard', 'calls, qualified leads, response time, at a glance'],
    ],
  },
  {
    icon: '\u{1F4AC}',
    title: 'Ask AI — Your Business, On Demand',
    blurb: 'A conversational assistant that actually knows your pipeline and can act on it.',
    items: [
      ['Ask AI', '"Show me every roofing lead this week," "Draft a follow-up text," answered instantly'],
      ['Tiered action-confirmation', 'safe changes happen instantly; anything customer-facing waits for your one-tap approval first'],
      ['AI Coaching', 'after-call feedback for your team, like "customer mentioned price 4x — offer financing"'],
    ],
  },
  {
    icon: '\u{1F5D3}️',
    title: 'Nothing Falls Through the Cracks',
    blurb: 'Every promise made on a call becomes a tracked commitment, automatically.',
    items: [
      ['Smart Timeline', 'the full story of every lead, call to close, in one view'],
      ['Automatic Follow-Up Detection', 'hears "call me Thursday" and creates the task itself'],
      ['Reminders for you and your customer', 'owner-facing digests, plus a customer-facing confirmation text'],
      ['Live In-Call Booking', 'a real Google Calendar invite, created while the customer is still on the phone'],
    ],
  },
];

const WAVE_HEIGHTS = [28, 44, 20, 52, 34, 60, 24, 48, 32, 56, 22, 40, 50, 26, 46];

export default function AiCallAssistantPage() {
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    let descTag = document.querySelector('meta[name="description"]');
    const previousDescription = descTag ? descTag.getAttribute('content') : null;
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.setAttribute('name', 'description');
      document.head.appendChild(descTag);
    }
    descTag.setAttribute('content', PAGE_DESCRIPTION);

    const robotsTag = document.createElement('meta');
    robotsTag.setAttribute('name', 'robots');
    robotsTag.setAttribute('content', 'noindex, follow');
    robotsTag.id = 'ai-call-assistant-noindex';
    document.head.appendChild(robotsTag);

    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.id = 'ai-call-assistant-faq-schema';
    ldScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    document.head.appendChild(ldScript);

    return () => {
      document.title = previousTitle;
      if (descTag && previousDescription !== null) descTag.setAttribute('content', previousDescription);
      const noindex = document.getElementById('ai-call-assistant-noindex');
      if (noindex) noindex.remove();
      const existing = document.getElementById('ai-call-assistant-faq-schema');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="gradient-bg min-h-screen isolate">
      <BackgroundEffects />
      <Navbar onContact={() => setIsContactOpen(true)} />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 8, repeat: Infinity }}
            className="absolute top-20 left-10 w-72 h-72 bg-primary-500/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.15, 0.1] }}
            transition={{ duration: 10, repeat: Infinity }}
            className="absolute bottom-20 right-10 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl"
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8 w-fit mx-auto">
            <span className="w-2 h-2 rounded-full bg-primary-500 shadow-[0_0_8px_#0ea5e9]" />
            <span className="text-sm text-gray-300">AI Call Assistant — Try It Live Right Now</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Your AI Voice Agent Doesn't Just Answer the Phone.{' '}
            <span className="gradient-text inline">It Closes the Loop.</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Most AI answering services just take a message and hand the follow-up back to you. Ours qualifies the
            lead in conversation, books the appointment, updates your CRM, and texts a confirmation — all in one
            call.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-primary-400">Inbound</span>
              <motion.a
                href={LIVE_PHONE_TEL}
                whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(14, 165, 233, 0.6)' }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg font-semibold text-lg flex items-center gap-2 hover:shadow-2xl transition-shadow cursor-pointer"
              >
                <Phone size={20} /> Call Now — {LIVE_PHONE_DISPLAY}
              </motion.a>
            </div>
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-accent-400">Outbound</span>
              <a
                href="#apply"
                className="px-8 py-4 border-2 border-primary-500 text-primary-500 rounded-lg font-semibold text-lg hover:bg-primary-500/10 transition-colors cursor-pointer"
              >
                Request a Callback
              </a>
            </div>
          </div>

          {/* Call-it-yourself widget */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl overflow-hidden max-w-xl mx-auto text-left"
          >
            <div className="p-6 sm:p-8 text-center">
              <div className="flex justify-between items-center mb-6 text-sm">
                <div className="font-bold text-white">
                  Primeslink <span className="text-primary-500">· AI Assistant</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="w-2 h-2 rounded-full bg-primary-500 shadow-[0_0_8px_#0ea5e9]" />
                  Live now
                </div>
              </div>

              <h3 className="text-primary-500 font-bold text-xl mb-6">Talk to Clara, Our Live AI Receptionist</h3>

              <div className="flex items-center justify-center gap-[3px] h-16 mb-6" aria-hidden="true">
                {WAVE_HEIGHTS.map((h, i) => (
                  <motion.span
                    key={i}
                    className="w-[3px] rounded-sm bg-gradient-to-b from-primary-500 to-accent-500"
                    style={{ height: h }}
                    animate={{ scaleY: [0.25, 1, 0.25] }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: i * 0.06 }}
                  />
                ))}
              </div>

              <p className="text-gray-400 text-sm max-w-md mx-auto">
                This is a real, live phone number. Call it and talk to Clara, our AI voice agent, yourself.
              </p>
            </div>
            <a
              href={LIVE_PHONE_TEL}
              className="flex items-center justify-center gap-2 py-4 px-6 font-semibold bg-gradient-to-r from-primary-500 to-primary-600 text-white"
            >
              <Phone size={18} /> Tap to Call Live — {LIVE_PHONE_DISPLAY}
            </a>
          </motion.div>
          <p className="text-xs text-gray-500 mt-4 mb-10">
            Standard call rates may apply. This is a real, working phone line.
          </p>

          {/* Proof points */}
          <div className="grid sm:grid-cols-3 gap-4 max-w-5xl mx-auto text-left">
            {TRUST_POINTS.map(({ icon: Icon, title, description }) => (
              <div key={title} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="w-9 h-9 rounded-full bg-accent-500/15 text-accent-500 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} />
                </div>
                <div>
                  <p className="text-white text-base font-semibold">{title}</p>
                  <p className="text-gray-400 text-sm leading-snug">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Positioning */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-14"
          >
            <div className="text-primary-500 font-bold text-sm tracking-wide uppercase mb-3">The Real Difference</div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
              A Message-Taking Service vs. an AI That Actually Works the Lead
            </h2>
            <p className="text-gray-400">Plenty of "AI receptionist" tools can pick up a call. Very few can finish the job.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="p-8 rounded-2xl border border-white/10 bg-white/5"
            >
              <h3 className="text-gray-400 font-bold text-lg mb-5">A service that "just takes a message"</h3>
              <ul className="space-y-3">
                {[
                  'Answers the call, jots down a note',
                  'You still call the lead back yourself',
                  'You still book the appointment yourself',
                  'You still update the CRM by hand',
                  'No record of what was actually said',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-gray-300 text-sm">
                    <XCircle size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="p-8 rounded-2xl border border-primary-500/40 bg-primary-500/5 shadow-[0_0_40px_rgba(14,165,233,0.08)]"
            >
              <h3 className="text-primary-500 font-bold text-lg mb-5">Primeslink AI Call Assistant</h3>
              <ul className="space-y-3">
                {[
                  'Answers, qualifies, and scores the lead in conversation',
                  'Books the appointment live, with a real calendar invite',
                  'Updates your CRM automatically — summary, score, next action',
                  "Texts the customer a confirmation, no one has to remember to",
                  'Every call recorded, transcribed, and searchable',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-gray-300 text-sm">
                    <CheckCircle2 size={16} className="text-primary-500 mt-0.5 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto mb-14"
          >
            <div className="text-primary-500 font-bold text-sm tracking-wide uppercase mb-3">What It Actually Does</div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
              Everything a Great Front-Desk Person Would Do — On Every Single Call
            </h2>
            <p className="text-gray-400">Every feature below is real and already shipped.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {FEATURE_GROUPS.map((group, idx) => (
              <motion.div
                key={group.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="p-8 rounded-2xl glass"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-5 bg-gradient-to-br from-primary-500/20 to-accent-500/20 border border-white/10">
                  {group.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{group.title}</h3>
                <p className="text-gray-400 text-sm mb-5">{group.blurb}</p>
                <ul>
                  {group.items.map(([label, desc], i) => (
                    <li
                      key={label}
                      className={`text-gray-300 text-sm py-3 ${i !== 0 ? 'border-t border-white/10' : ''}`}
                    >
                      <strong className="text-white font-semibold">{label}</strong> — {desc}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-xl mx-auto mb-4"
          >
            <div className="text-primary-500 font-bold text-sm tracking-wide uppercase mb-3">See It Live</div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-white">Here's What the Dashboard Actually Looks Like</h2>
          </motion.div>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
            This is a real, <strong className="text-white">fully functional build</strong> for the insurance
            industry, built on the same technology we'd implement for your business. Below is what a qualified lead
            actually looks like on the dashboard.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass rounded-2xl p-6"
            >
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-3">AI Qualification — Life Insurance Inquiry</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-4xl font-extrabold text-primary-500">75</span>
                <span className="text-gray-400 text-sm">/ 100</span>
              </div>
              <p className="text-gray-300 text-sm mb-2">$2M / 20-year term, existing customer looking to upgrade coverage.</p>
              <span className="inline-block text-xs px-3 py-1 rounded-full bg-accent-500/15 text-pink-400 border border-accent-500/30">
                Ready to Buy
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="glass rounded-2xl p-6"
            >
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-3">Ask AI</div>
              <div className="flex flex-col gap-2">
                <div className="self-end max-w-[88%] rounded-xl px-3 py-2 text-sm bg-primary-500/20">
                  Show me every life insurance lead scored above 70 this week
                </div>
                <div className="self-start max-w-[88%] rounded-xl px-3 py-2 text-sm bg-white/5 border border-white/10">
                  Found 3 — including a $2M/20yr term inquiry scored 75, marked "Ready to Buy." Want me to draft a
                  follow-up text?
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="glass rounded-2xl p-6"
            >
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-4">Smart Timeline</div>
              <div className="flex flex-col gap-4">
                {[
                  ['Inbound call answered by AI', '2:14 PM'],
                  ['Qualified & scored 75/100', '2:19 PM'],
                  ['Follow-up task created — "call Thursday"', '2:19 PM'],
                  ['Confirmation text sent to customer', '2:20 PM'],
                ].map(([text, time]) => (
                  <div key={text} className="flex gap-3 items-start">
                    <span className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0 shadow-[0_0_6px_#0ea5e9]" />
                    <div>
                      <p className="text-gray-300 text-sm">{text}</p>
                      <span className="text-xs text-gray-500">{time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <p className="text-center text-gray-400 text-sm">
            Our platform already supports <strong className="text-white">30+ industries</strong>, each with its own
            tailored setup.
          </p>
        </div>
      </section>

      {/* Built For */}
      <section className="pt-8 pb-8 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-xl mx-auto"
        >
          <div className="text-primary-500 font-bold text-sm tracking-wide uppercase mb-3">Built For</div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-white">Any High-Ticket, Service-Based Business</h2>
          <p className="text-gray-400">If a missed call can cost you a real sale, this was built for you.</p>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="text-primary-500 font-bold text-sm tracking-wide uppercase mb-3">FAQ</div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white">Questions Business Owners Actually Ask</h2>
          </motion.div>

          <div className="flex flex-col gap-3">
            {FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={faq.q} className="glass rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full text-left px-6 py-5 flex justify-between items-center gap-4 text-white font-semibold"
                  >
                    {faq.q}
                    <motion.span animate={{ rotate: isOpen ? 45 : 0 }} className="text-primary-500 flex-shrink-0">
                      <Plus size={20} />
                    </motion.span>
                  </button>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <p className="px-6 pb-5 text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA — apply form */}
      <section id="apply" className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-xl mx-auto mb-10"
        >
          <div className="text-primary-500 font-bold text-sm tracking-wide uppercase mb-3">Get Started</div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
            Talk to <span className="gradient-text">Clara</span> Today
          </h2>
          <p className="text-gray-400">
            Call now for an instant answer, or tell us what you need and one of our AI voice assistants will call
            you back.
          </p>
        </motion.div>

        <AiCallAssistantApplyForm />

        <p className="text-center text-gray-500 text-sm mt-8">
          Prefer a scheduled walkthrough instead?{' '}
          <button onClick={() => setIsCalendlyOpen(true)} className="text-primary-500 font-semibold hover:underline">
            Book a free demo
          </button>
          .
        </p>
      </section>

      <Footer
        onContact={() => setIsContactOpen(true)}
        onTerms={() => setIsTermsOpen(true)}
      />

      <CalendlyEmbed isOpen={isCalendlyOpen} onClose={() => setIsCalendlyOpen(false)} />
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
      <TermsOfUse isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </div>
  );
}
